import React, { useState, useEffect } from "react";
import {
  Card,
  DatePicker,
  Switch,
  Button,
  Row,
  Col,
  Statistic,
  message,
  Modal,
  Table,
  Select
} from "antd";
import { FileTextOutlined, DollarOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { ConfigProvider } from "antd";
import enUS from "antd/es/locale/en_US";
import billingApi from "../../billing/store/api";
import GenerateSaasModal from "./GenerateSaasModal";
import GeneratePoolModal from "./GeneratePoolModal";

const { RangePicker } = DatePicker;

// Create custom locale with Monday as first day of week
const mondayFirstLocale = {
  ...enUS,
  lang: {
    ...enUS.lang,
    weekStart: 1
  },
  DatePicker: {
    ...enUS.DatePicker,
    lang: {
      ...enUS.DatePicker.lang,
      weekStart: 1
    }
  }
};

// Custom RangePicker component with Monday-first week
const MondayFirstRangePicker = (props) => {
  // Create a temporary moment locale for this component
  const tempLocale = moment.localeData()._config;

  React.useEffect(() => {
    // Temporarily set Monday as first day
    moment.updateLocale("en", {
      week: {
        dow: 1, // Monday
        doy: 4
      }
    });

    return () => {
      // Restore original locale on cleanup
      moment.updateLocale("en", tempLocale);
    };
  }, []);

  return (
    <ConfigProvider locale={mondayFirstLocale}>
      <RangePicker {...props} />
    </ConfigProvider>
  );
};

const FacturasDashboard = () => {
  const history = useHistory();

  // Generate Invoices state
  const [autoGenerateNightly, setAutoGenerateNightly] = useState(false);
  const [autoGenerateWeekly, setAutoGenerateWeekly] = useState(false);
  const [autoGenerateMonthlyEntitlements, setAutoGenerateMonthlyEntitlements] =
    useState(false);
  const [invoiceDateRange, setInvoiceDateRange] = useState([
    moment().startOf("isoWeek"),
    moment().endOf("isoWeek")
  ]);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [loadingWeeklySettings, setLoadingWeeklySettings] = useState(false);
  const [loadingEntitlementsSettings, setLoadingEntitlementsSettings] = useState(false);
  const [routesModalVisible, setRoutesModalVisible] = useState(false);
  const [saasModalVisible, setSaasModalVisible] = useState(false);
  const [poolModalVisible, setPoolModalVisible] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [loadingPool, setLoadingPool] = useState(false);
  const [loadingEntitlements, setLoadingEntitlements] = useState(false);
  const [routesData, setRoutesData] = useState(null);
  const [completedOnly, setCompletedOnly] = useState(true);
  const [emailClients, setEmailClients] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loadingGeneration, setLoadingGeneration] = useState(false);

  // Load auto-generate settings on component mount
  useEffect(() => {
    loadAutoGenerateSettings();
  }, []);

  const loadAutoGenerateSettings = async () => {
    try {
      // Load nightly setting
      await billingApi.getAutoGenerateInvoicesNightly();
      setAutoGenerateNightly(false); // Temporarily disabled for visual purposes

      // Load weekly setting
      await billingApi.getAutoGenerateInvoicesWeekly();
      setAutoGenerateWeekly(false); // Temporarily disabled for visual purposes

      // Load monthly entitlements setting
      await billingApi.getAutoGenerateInvoicesMonthlyEntitlements();
      setAutoGenerateMonthlyEntitlements(false); // Temporarily disabled for visual purposes
    } catch (error) {
      console.error("Failed to load auto-generate settings:", error);
    }
  };

  const handleAutoGenerateToggle = async (checked) => {
    setLoadingSettings(true);
    try {
      await billingApi.setAutoGenerateInvoicesNightly(checked);
      setAutoGenerateNightly(checked);
      message.success(
        `Auto-generate invoices nightly for Managed clients ${
          checked ? "enabled" : "disabled"
        }`
      );
    } catch (error) {
      console.error("Failed to update auto-generate setting:", error);
      message.error("Failed to update setting");
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleAutoGenerateWeeklyToggle = async (checked) => {
    setLoadingWeeklySettings(true);
    try {
      await billingApi.setAutoGenerateInvoicesWeekly(checked);
      setAutoGenerateWeekly(checked);
      message.success(
        `Auto-generate invoices weekly for Managed clients ${
          checked ? "enabled" : "disabled"
        }`
      );
    } catch (error) {
      console.error("Failed to update weekly auto-generate setting:", error);
      message.error("Failed to update setting");
    } finally {
      setLoadingWeeklySettings(false);
    }
  };

  const handleAutoGenerateEntitlementsToggle = async (checked) => {
    setLoadingEntitlementsSettings(true);
    try {
      await billingApi.setAutoGenerateInvoicesMonthlyEntitlements(checked);
      setAutoGenerateMonthlyEntitlements(checked);
      message.success(
        `Auto-generate invoices monthly for Entitlements ${
          checked ? "enabled" : "disabled"
        }`
      );
    } catch (error) {
      console.error("Failed to update entitlements auto-generate setting:", error);
      message.error("Failed to update setting");
    } finally {
      setLoadingEntitlementsSettings(false);
    }
  };

  // Loaded without a client filter on purpose: the table is filtered client-side and
  // the Client Filter options are built from whatever this date range returned.
  const loadManagedRoutes = async (dateRange, completed) => {
    if (!dateRange || dateRange.length !== 2) {
      message.error("Please select a valid date range");
      return null;
    }

    setLoadingRoutes(true);
    try {
      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");
      const response = await billingApi.getManagedRoutesForInvoice(
        startDate,
        endDate,
        completed
      );
      setRoutesData(response);
      return response;
    } catch (error) {
      console.error("Failed to load managed routes:", error);
      message.error("Failed to load managed routes");
      return null;
    } finally {
      setLoadingRoutes(false);
    }
  };

  const handleGenerateManagedRoutes = async () => {
    const response = await loadManagedRoutes(invoiceDateRange, completedOnly);
    if (response) {
      setRoutesModalVisible(true);
    }
  };

  // Only the clients with routes still to be invoiced in this date range, so it's
  // clear who is left to bill instead of listing every client on the platform.
  const clientsWithRoutes = React.useMemo(() => {
    const byId = new Map();
    (routesData?.routes || []).forEach((route) => {
      if (route.clientId && !byId.has(route.clientId)) {
        byId.set(route.clientId, {
          id: route.clientId,
          clientName: route.client || "N/A"
        });
      }
    });
    return [...byId.values()].sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [routesData]);

  const handleGeneratePool = async () => {
    if (!invoiceDateRange || invoiceDateRange.length !== 2) {
      message.error("Please select a valid date range");
      return;
    }

    setLoadingPool(true);
    try {
      const startDate = invoiceDateRange[0].format("YYYY-MM-DD");
      const endDate = invoiceDateRange[1].format("YYYY-MM-DD");
      await billingApi.generatePoolInvoices(startDate, endDate);
      message.success("Pool invoices generated successfully");
    } catch (error) {
      console.error("Failed to generate pool invoices:", error);
      message.error("Failed to generate pool invoices");
    } finally {
      setLoadingPool(false);
    }
  };

  const handleGenerateEntitlements = async () => {
    if (!invoiceDateRange || invoiceDateRange.length !== 2) {
      message.error("Please select a valid date range");
      return;
    }

    setLoadingEntitlements(true);
    try {
      const startDate = invoiceDateRange[0].format("YYYY-MM-DD");
      const endDate = invoiceDateRange[1].format("YYYY-MM-DD");
      await billingApi.generateEntitlementsInvoices(startDate, endDate);
      message.success("Entitlements invoices generated successfully");
    } catch (error) {
      console.error("Failed to generate entitlements invoices:", error);
      message.error("Failed to generate entitlements invoices");
    } finally {
      setLoadingEntitlements(false);
    }
  };

  const handleGenerateManagedInvoices = async () => {
    if (!invoiceDateRange || invoiceDateRange.length !== 2) {
      message.error("Please select a valid date range");
      return;
    }

    setLoadingGeneration(true);
    try {
      const startDate = invoiceDateRange[0].format("YYYY-MM-DD");
      const endDate = invoiceDateRange[1].format("YYYY-MM-DD");
      const response = await billingApi.generateManagedRoutesInvoices(
        startDate,
        endDate,
        completedOnly,
        emailClients,
        selectedClient
      );

      const { generatedCount, emailedCount } = response;
      let successMessage = `Successfully generated ${generatedCount} invoice${
        generatedCount !== 1 ? "s" : ""
      }`;
      if (emailClients && emailedCount > 0) {
        successMessage += ` and emailed ${emailedCount} client${
          emailedCount !== 1 ? "s" : ""
        }`;
      }
      message.success(successMessage);

      // Refresh the routes data
      loadManagedRoutes(invoiceDateRange, completedOnly);
    } catch (error) {
      console.error("Failed to generate managed routes invoices:", error);
      message.error("Failed to generate invoices");
    } finally {
      setLoadingGeneration(false);
    }
  };

  return (
    <div className="facturas-dashboard" style={{ padding: "24px" }}>
      <Card title="Generate Invoices" style={{ marginBottom: 24 }}>
        {/* Autogeneration Sub-section */}
        <Card
          type="inner"
          title="Autogeneration (Coming Soon)"
          style={{ marginBottom: 16 }}
          headStyle={{ backgroundColor: "#fafafa", fontSize: "14px" }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 8, fontWeight: 500, color: "#bbb" }}>
                Auto-generate nightly for Managed clients
              </div>
              <div style={{ display: "flex", alignItems: "center", height: 32 }}>
                <Switch
                  checked={autoGenerateNightly}
                  onChange={handleAutoGenerateToggle}
                  loading={loadingSettings}
                  disabled={true}
                  style={{ marginRight: 8 }}
                />
                <span>{autoGenerateNightly ? "ON" : "OFF"}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 8, fontWeight: 500, color: "#bbb" }}>
                Auto-generate weekly for Managed clients
              </div>
              <div style={{ display: "flex", alignItems: "center", height: 32 }}>
                <Switch
                  checked={autoGenerateWeekly}
                  onChange={handleAutoGenerateWeeklyToggle}
                  loading={loadingWeeklySettings}
                  disabled={true}
                  style={{ marginRight: 8 }}
                />
                <span>{autoGenerateWeekly ? "ON" : "OFF"}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 8, fontWeight: 500, color: "#bbb" }}>
                Auto-generate monthly for Entitlements
              </div>
              <div style={{ display: "flex", alignItems: "center", height: 32 }}>
                <Switch
                  checked={autoGenerateMonthlyEntitlements}
                  onChange={handleAutoGenerateEntitlementsToggle}
                  loading={loadingEntitlementsSettings}
                  disabled={true}
                  style={{ marginRight: 8 }}
                />
                <span>{autoGenerateMonthlyEntitlements ? "ON" : "OFF"}</span>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Manual Generation Sub-section */}
        <Card
          type="inner"
          title="Manual Generation"
          headStyle={{ backgroundColor: "#fafafa", fontSize: "14px" }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={6}>
              <div style={{ marginBottom: 8 }}>&nbsp;</div>
              <Button
                type="primary"
                icon={<FileTextOutlined />}
                onClick={handleGenerateManagedRoutes}
                loading={loadingRoutes}
                style={{ width: "100%" }}
              >
                Generate: Managed Routes
              </Button>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ marginBottom: 8 }}>&nbsp;</div>
              <Button
                type="primary"
                icon={<FileTextOutlined />}
                onClick={() => setSaasModalVisible(true)}
                style={{ width: "100%" }}
              >
                Generate: SaaS
              </Button>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ marginBottom: 8 }}>&nbsp;</div>
              <Button
                type="primary"
                icon={<FileTextOutlined />}
                onClick={() => setPoolModalVisible(true)}
                style={{ width: "100%" }}
              >
                Generate: Pool
              </Button>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ marginBottom: 8 }}>&nbsp;</div>
              <Button
                type="default"
                icon={<FileTextOutlined />}
                onClick={handleGenerateEntitlements}
                loading={loadingEntitlements}
                disabled={true}
                style={{ width: "100%" }}
              >
                Generate: Entitlements
              </Button>
            </Col>
          </Row>
        </Card>
      </Card>

      {/* Generate Managed Routes Modal */}
      <Modal
        title="Generate Managed Routes"
        open={routesModalVisible}
        onCancel={() => setRoutesModalVisible(false)}
        footer={null}
        width="90%"
        style={{ maxWidth: "1400px" }}
      >
        Will generate invoices for all routes, for clients = managed (all clients unless
        you update them to SaaS!), and route status = closed during the date range you
        choose.
        {/* Filters Section */}
        <Card title="Filters" style={{ marginBottom: 16, marginTop: 16 }}>
          <Row gutter={[24, 16]}>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Date Range</div>
              <MondayFirstRangePicker
                value={invoiceDateRange}
                onChange={(newRange) => {
                  setInvoiceDateRange(newRange);
                  // Refresh the routes data when date range changes
                  if (newRange && newRange.length === 2) {
                    loadManagedRoutes(newRange, completedOnly);
                  }
                }}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
              />
            </Col>

            <Col xs={24} md={12}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Client Filter</div>
              <Select
                style={{ width: "100%" }}
                placeholder="All clients"
                allowClear
                showSearch
                value={selectedClient}
                onChange={setSelectedClient}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {clientsWithRoutes.map((client) => (
                  <Select.Option key={client.id} value={client.id}>
                    {client.clientName}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          </Row>

          <Row gutter={[24, 16]} style={{ marginTop: 16 }}>
            <Col xs={12} sm={8}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Route Status</div>
              <div style={{ display: "flex", alignItems: "center", height: 32 }}>
                <Switch
                  checked={completedOnly}
                  onChange={(checked) => {
                    setCompletedOnly(checked);
                    // Refresh the routes data when filter changes
                    if (invoiceDateRange && invoiceDateRange.length === 2) {
                      loadManagedRoutes(invoiceDateRange, checked);
                    }
                  }}
                  style={{ marginRight: 8 }}
                />
                <span>{completedOnly ? "Completed only" : "All statuses"}</span>
              </div>
            </Col>

            <Col xs={12} sm={8}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Email Notifications</div>
              <div style={{ display: "flex", alignItems: "center", height: 32 }}>
                <Switch
                  checked={emailClients}
                  onChange={setEmailClients}
                  style={{ marginRight: 8 }}
                />
                <span>{emailClients ? "Email clients" : "No emails"}</span>
              </div>
            </Col>

            <Col xs={24} sm={8}>
              <div style={{ marginBottom: 8 }}>&nbsp;</div>
              <Button
                type="primary"
                icon={<FileTextOutlined />}
                onClick={handleGenerateManagedInvoices}
                loading={loadingGeneration}
                style={{ width: "100%", height: 40 }}
                size="large"
              >
                Generate Invoices
              </Button>
            </Col>
          </Row>
        </Card>
        {routesData &&
          (() => {
            // Filter routes based on completedOnly toggle and selected client
            let filteredRoutes = routesData.routes;

            // Apply completed filter
            if (completedOnly) {
              filteredRoutes = filteredRoutes.filter(
                (route) => route.status && route.status.toLowerCase() === "completed"
              );
            }

            // Apply client filter
            if (selectedClient) {
              filteredRoutes = filteredRoutes.filter(
                (route) => route.clientId === selectedClient
              );
            }

            // Calculate filtered summary statistics
            const uniqueClients = new Set(filteredRoutes.map((route) => route.client))
              .size;
            const totalRevenue = filteredRoutes.reduce(
              (sum, route) => sum + (parseFloat(route.clientFinalPrice) || 0),
              0
            );

            return (
              <>
                {/* Summary Section */}
                <Card style={{ marginBottom: 16 }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={6}>
                      <Statistic
                        title="Date Range"
                        value={routesData.summary.dateRange}
                        valueStyle={{ fontSize: 14, color: "#666" }}
                      />
                    </Col>
                    <Col xs={24} sm={6}>
                      <Statistic
                        title="Total Clients"
                        value={uniqueClients}
                        valueStyle={{ color: "#1890ff" }}
                      />
                    </Col>
                    <Col xs={24} sm={6}>
                      <Statistic
                        title="Total Routes"
                        value={filteredRoutes.length}
                        valueStyle={{ color: "#52c41a" }}
                      />
                    </Col>
                    <Col xs={24} sm={6}>
                      <Statistic
                        title="Total Revenue"
                        value={totalRevenue}
                        precision={2}
                        prefix={<DollarOutlined />}
                        valueStyle={{ color: "#722ed1" }}
                      />
                    </Col>
                  </Row>
                </Card>

                {/* Routes Table */}
                <Table
                  dataSource={filteredRoutes}
                  rowKey="id"
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 800 }}
                  onRow={(record) => ({
                    onClick: () => {
                      window.open(`/admin/routes/${record.id}`, "_blank");
                    }
                  })}
                  style={{ cursor: "pointer" }}
                  columns={[
                    {
                      title: "ID",
                      dataIndex: "id",
                      width: 120,
                      render: (value) => (
                        <span style={{ color: "#1f3998", fontWeight: 500 }}>
                          {value.substr(0, 8).toUpperCase()}
                        </span>
                      )
                    },
                    {
                      title: "Client",
                      dataIndex: "client",
                      width: 150
                    },
                    {
                      title: "# of stops",
                      dataIndex: "numberOfStops",
                      width: 100,
                      align: "center"
                    },
                    {
                      title: "Status",
                      dataIndex: "status",
                      width: 120,
                      render: (value) => (
                        <span
                          style={{
                            textTransform: "capitalize",
                            color:
                              value === "completed"
                                ? "#52c41a"
                                : value === "canceled"
                                ? "#ff4d4f"
                                : "#1890ff"
                          }}
                        >
                          {value}
                        </span>
                      )
                    },
                    {
                      title: "Client Final Price",
                      dataIndex: "clientFinalPrice",
                      width: 150,
                      align: "right",
                      render: (value) => (
                        <span style={{ fontWeight: 500 }}>
                          ${parseFloat(value || 0).toFixed(2)}
                        </span>
                      )
                    }
                  ]}
                />
              </>
            );
          })()}
      </Modal>

      {/* Generate SaaS Modal */}
      <GenerateSaasModal
        visible={saasModalVisible}
        onCancel={() => setSaasModalVisible(false)}
      />

      {/* Generate Pool Modal */}
      <GeneratePoolModal
        visible={poolModalVisible}
        onCancel={() => setPoolModalVisible(false)}
      />

      {/* See Old Invoice Screen Button */}
      <div
        style={{
          textAlign: "center",
          marginTop: 24,
          paddingTop: 24,
          borderTop: "1px solid #f0f0f0"
        }}
      >
        {/*}
        <Button
          type="default"
          onClick={() => history.push("/admin/invoicing")}
          style={{ minWidth: 200 }}
        >
          Invoices (old version)
        </Button>
        */}
      </div>
    </div>
  );
};

export default FacturasDashboard;
